/* eslint-disable no-ex-assign */
import { createModel } from "@rematch/core";
import { Pets } from "../contract/types/ethers-contracts/Pets";
import { Loot__factory } from "../contract/types/ethers-contracts/factories/Loot__factory";
import { Loot } from "../contract/types/ethers-contracts/Loot";
import { Mloot__factory } from "../contract/types/ethers-contracts/factories/Mloot__factory";
import { Mloot } from "../contract/types/ethers-contracts/Mloot";
import { RolePlusLoot__factory } from "../contract/types/ethers-contracts/factories/RolePlusLoot__factory";
import { RolePlusLoot } from "../contract/types/ethers-contracts/RolePlusLoot";
import { RootModel } from ".";

import { MAIN_CONTRACT, RINKEBY_CONTRACT } from "../config/contract";

const CONFIG = MAIN_CONTRACT;

export interface PetsTokenList {
  id?: number;
  alignment: string;
  species: string;
  trait1: string;
  trait2: string;
  trait3: string;
  URI?: {
    name: string;
    description: string;
    image: string;
    attributes: {
      trait_type: string;
      value: string;
    }[];
  };
}

export interface LootTokenList {
  id?: number;
  chest: string;
  foot: string;
  hand: string;
  head: string;
  neck: string;
  ring: string;
  waist: string;
  weapon: string;
  URI?: {
    name: string;
    description: string;
    image: string;
    attributes: {
      trait_type: string;
      value: string;
    }[];
  };
}

export interface RoleTokenList {
  id?: number;
  alignment: string;
  occupation: string;
  races: string;
  gender: string;
  trait1: string;
  trait2: string;
  trait3: string;
  URI?: {
    name: string;
    description: string;
    image: string;
    attributes: {
      trait_type: string;
      value: string;
    }[];
  };
}

interface rolePlusState {
  PetsContract?: Pets;
  MlootContract?: Mloot;
  LootContract?: Loot;
  RolePlusContract?: RolePlusLoot;
  roleDailyMint: number;
  PetsTokenList?: PetsTokenList[];
  RoleTokenList?: RoleTokenList[];
  LootTokenList?: LootTokenList[];
  MlootTokenList?: LootTokenList[];
  RolePlusTokenList?: {
    id: number;
    URI: {
      name: string;
      description: string;
      image: string;
      attributes: {
        trait_type: string;
        value: string;
      }[];
    };
  }[];
}

const initMainState: rolePlusState = {
  roleDailyMint: 0,
};

export const rolePlus = createModel<RootModel>()({
  state: initMainState,
  reducers: {
    setFields(state, fields: Partial<rolePlusState>): rolePlusState {
      return {
        ...state,
        ...fields,
      };
    },
  },
  effects: (dispatch) => ({
    async initRolePlus(payload, state) {
      let { ethersProvider, signer } = state.main;
      if (ethersProvider && signer) {
        // 连接合约
        let LootContract = Loot__factory.connect(CONFIG.loot, signer);
        let RolePlusContract = RolePlusLoot__factory.connect(
          CONFIG.rolePlus,
          signer
        );
        let MlootContract = Mloot__factory.connect(CONFIG.mloot, signer);
        dispatch.rolePlus.setFields({
          LootContract,
          MlootContract,
          RolePlusContract,
        });
      }
    },
    async getPetURI(tokenId: number, state) {
      const { PetsContract } = state.pets;
      let [URI, alignment, species, trait1, trait2, trait3] = await Promise.all(
        [
          PetsContract?.tokenURI(tokenId),
          PetsContract?.getAlignment(tokenId),
          PetsContract?.getSpecies(tokenId),
          PetsContract?.getTrait(tokenId, 1),
          PetsContract?.getTrait(tokenId, 2),
          PetsContract?.getTrait(tokenId, 3),
        ]
      );
      return {
        id: tokenId,
        URI,
        alignment,
        species,
        trait1,
        trait2,
        trait3,
      };
    },
    async getPlusURI(tokenId: number, state): Promise<any> {
      const { RolePlusContract } = state.rolePlus;
      let [URI] = await Promise.all([RolePlusContract?.tokenURI(tokenId)]);
      return {
        id: tokenId,
        URI,
      };
    },
    async getRoleURI(tokenId: number, state): Promise<any> {
      const { RoleContract } = state.main;
      let [URI, alignment, gender, races, occupation, trait1, trait2, trait3] =
        await Promise.all([
          RoleContract?.tokenURI(tokenId),
          RoleContract?.getAlignment(tokenId),
          RoleContract?.getGender(tokenId),
          RoleContract?.getRace(tokenId),
          RoleContract?.getOccupation(tokenId),
          RoleContract?.getTrait1(tokenId),
          RoleContract?.getTrait2(tokenId),
          RoleContract?.getTrait3(tokenId),
        ]);
      return {
        id: tokenId,
        URI,
        alignment,
        races,
        gender,
        occupation,
        trait1,
        trait2,
        trait3,
      };
    },
    async getLootURI(tokenId: number, state): Promise<any> {
      const { LootContract } = state.rolePlus;
      let [URI, chest, foot, hand, head, neck, ring, waist, weapon] =
        await Promise.all([
          LootContract?.tokenURI(tokenId),
          LootContract?.getChest(tokenId),
          LootContract?.getFoot(tokenId),
          LootContract?.getHand(tokenId),
          LootContract?.getHead(tokenId),
          LootContract?.getNeck(tokenId),
          LootContract?.getRing(tokenId),
          LootContract?.getWaist(tokenId),
          LootContract?.getWeapon(tokenId),
        ]);
      return {
        id: tokenId,
        URI,
        chest,
        foot,
        hand,
        head,
        neck,
        ring,
        waist,
        weapon,
      };
    },
    async getMlootURI(tokenId: number, state): Promise<any> {
      const { MlootContract } = state.rolePlus;
      let [URI, chest, foot, hand, head, neck, ring, waist, weapon] =
        await Promise.all([
          MlootContract?.tokenURI(tokenId),
          MlootContract?.getChest(tokenId),
          MlootContract?.getFoot(tokenId),
          MlootContract?.getHand(tokenId),
          MlootContract?.getHead(tokenId),
          MlootContract?.getNeck(tokenId),
          MlootContract?.getRing(tokenId),
          MlootContract?.getWaist(tokenId),
          MlootContract?.getWeapon(tokenId),
        ]);
      return {
        id: tokenId,
        URI,
        chest,
        foot,
        hand,
        head,
        neck,
        ring,
        waist,
        weapon,
      };
    },
    async getRolePlusURI(payload, state) {
      const { RolePlusContract } = state.rolePlus;
      const { userInfo } = state.main;
      if (RolePlusContract && userInfo.address) {
        let res = await RolePlusContract.balanceOf(userInfo.address);
        let count = res.toNumber();
        let tokenIdList: number[] = [];
        for (let index = 0; index < count; index++) {
          let tokenId = await RolePlusContract.tokenOfOwnerByIndex(
            userInfo.address,
            index
          );
          tokenIdList.push(tokenId.toNumber());
        }
        let getURIPromise = tokenIdList.map((tokenId) => {
          return dispatch.rolePlus.getPlusURI(tokenId);
        });
        let URIRes = await Promise.all(getURIPromise);
        let RolePlusTokenList: any = URIRes.map((u) => {
          u.URI = JSON.parse(atob(u.URI!.substring(29)));
          return u;
        });
        dispatch.rolePlus.setFields({
          RolePlusTokenList,
        });
      }
    },
    async getRolesURI(payload, state) {
      const { RoleContract } = state.main;
      const { RolePlusContract } = state.rolePlus;
      const { userInfo } = state.main;
      if (RolePlusContract && userInfo.address) {
        let res = await RolePlusContract.balanceOf(userInfo.address);
        let count = res.toNumber();
        let tokenIdList: number[] = [];
        for (let index = 0; index < count; index++) {
          let tokenId = await RolePlusContract.tokenOfOwnerByIndex(
            userInfo.address,
            index
          );
          tokenIdList.push(tokenId.toNumber());
        }
        let getURIPromise = tokenIdList.map((tokenId) => {
          return dispatch.rolePlus.getRoleURI(tokenId);
        });
        let URIRes = await Promise.all(getURIPromise);
        let RoleTokenList: any = URIRes.map((u) => {
          u.URI = JSON.parse(atob(u.URI!.substring(29)));
          return u;
        });
        dispatch.rolePlus.setFields({
          RoleTokenList,
        });
      }
    },
    async getPetsURI(payload, state) {
      const { PetsContract } = state.pets;
      const { userInfo } = state.main;
      if (PetsContract && userInfo.address) {
        let res = await PetsContract.balanceOf(userInfo.address);
        let count = res.toNumber();
        let tokenIdList: number[] = [];
        for (let index = 0; index < count; index++) {
          let tokenId = await PetsContract.tokenOfOwnerByIndex(
            userInfo.address,
            index
          );
          tokenIdList.push(tokenId.toNumber());
        }
        let getURIPromise = tokenIdList.map((tokenId) => {
          return dispatch.rolePlus.getPetURI(tokenId);
        });
        let URIRes = await Promise.all(getURIPromise);
        let PetsTokenList: any = URIRes.map((u) => {
          u.URI = JSON.parse(atob(u.URI!.substring(29)));
          return u;
        });
        dispatch.rolePlus.setFields({
          PetsTokenList,
        });
      }
    },
    async getLootsURI(payload, state) {
      const { LootContract } = state.rolePlus;
      const { userInfo } = state.main;
      let LootTokenList = state.rolePlus.LootTokenList;
      if (LootContract && userInfo.address) {
        let res = await LootContract.balanceOf(userInfo.address);
        let count = res.toNumber();
        let tokenIdList: number[] = [];
        for (let index = 0; index < count; index++) {
          let tokenId = await LootContract.tokenOfOwnerByIndex(
            userInfo.address,
            index
          );
          tokenIdList.push(tokenId.toNumber());
        }
        let getURIPromise = tokenIdList.map((tokenId) => {
          return dispatch.rolePlus.getLootURI(tokenId);
        });
        let URIRes = await Promise.all(getURIPromise);
        let tempLootTokenList: any = URIRes.map((u) => {
          u.URI = JSON.parse(atob(u.URI!.substring(29)));
          return u;
        });
        if (!LootTokenList) {
          LootTokenList = [];
        }
        dispatch.rolePlus.setFields({
          LootTokenList: [...LootTokenList, ...tempLootTokenList],
        });
      }
    },
    async getMlootsURI(payload, state) {
      const { MlootContract } = state.rolePlus;
      const { userInfo } = state.main;
      let LootTokenList = state.rolePlus.LootTokenList;
      if (MlootContract && userInfo.address) {
        let res = await MlootContract.balanceOf(userInfo.address);
        let count = res.toNumber();
        let tokenIdList: number[] = [];
        for (let index = 0; index < count; index++) {
          let tokenId = await MlootContract.tokenOfOwnerByIndex(
            userInfo.address,
            index
          );
          tokenIdList.push(tokenId.toNumber());
        }
        let getURIPromise = tokenIdList.map((tokenId) => {
          return dispatch.rolePlus.getMlootURI(tokenId);
        });
        let URIRes = await Promise.all(getURIPromise);
        let MlootTokenList: any = URIRes.map((u) => {
          u.URI = JSON.parse(atob(u.URI!.substring(29)));
          return u;
        });
        if (!LootTokenList) {
          LootTokenList = [];
        }
        dispatch.rolePlus.setFields({
          LootTokenList: [...LootTokenList, ...MlootTokenList],
        });
      }
    },
    async claimRolePlus(
      {
        lootId,
        petId,
        roleId,
        imageUrl,
      }: { lootId: number; petId: number; roleId: number; imageUrl: string },
      state
    ) {
      const { RolePlusContract } = state.rolePlus;
      if (RolePlusContract) {
        let mintHash;
        try {
          mintHash = await RolePlusContract.claim(
            lootId,
            petId,
            roleId,
            imageUrl || ""
          );
          let result = await mintHash.wait();
          if (result.status === 1) {
            dispatch.main.openAlert({
              type: "success",
              content: "Congratulations! Transaction Confrimed!",
            });
            dispatch.main.setFields({
              party: true,
            });
          }
        } catch (MetaMaskError: any) {
          const { error } = JSON.parse(JSON.stringify(MetaMaskError));
          if (MetaMaskError?.code === 4001) {
            // 用户取消交易
            dispatch.main.openAlert({
              type: "error",
              content: MetaMaskError.message,
            });
          } else {
            dispatch.main.openAlert({
              type: "error",
              content: error?.message,
            });
          }
        }
      }
    },
    async changeImageUrl(
      { tokenId, imageUrl }: { tokenId: number; imageUrl: string },
      state
    ) {
      const { RolePlusContract } = state.rolePlus;
      if (RolePlusContract) {
        let mintHash;
        try {
          mintHash = await RolePlusContract.setImageUrl(
            tokenId,
            imageUrl || ""
          );
          let result = await mintHash.wait();
          if (result.status === 1) {
            dispatch.main.openAlert({
              type: "success",
              content: "Congratulations! Transaction Confrimed!",
            });
            dispatch.rolePlus.getRolePlusURI();
          }
        } catch (MetaMaskError: any) {
          const { error } = JSON.parse(JSON.stringify(MetaMaskError));
          if (MetaMaskError?.code === 4001) {
            // 用户取消交易
            dispatch.main.openAlert({
              type: "error",
              content: MetaMaskError.message,
            });
          } else {
            dispatch.main.openAlert({
              type: "error",
              content: error?.message,
            });
          }
        }
      }
    },
  }),
});
